/**
 * pcard-image-bubbles.js
 *
 * Hover image preview for product cards. The behavior is chosen by the theme
 * setting `pcard_image_preview` (None | Image Bubbles | Cycle On Hover |
 * Preview Dots), which card-product.liquid surfaces on the image wrapper as
 * data-image-preview="bubbles" | "cycle" | "dots".
 *
 *   - "bubbles": circular image previews below the image. Hovering a bubble
 *     swaps the displayed image; leaving the strip resets to the first image.
 *
 *   - "dots": a minimal dot per image, hidden until the card is hovered.
 *     Hovering a dot swaps the displayed image. Same swap mechanism as
 *     bubbles — only the indicator differs.
 *
 *   - "cycle": while the pointer is over the card's image area, the displayed
 *     image automatically CROSSFADES through the product's images on a timer,
 *     then resets to the first image when the pointer leaves. Pointer/hover
 *     devices only — touch devices show the static first image.
 *
 * All modes reuse the SAME stacked image markup (.product-card__image--bubble,
 * toggled via --active / --hidden) and the SAME switchImage() helper. The
 * wrapper only carries data-image-preview when the product has 2+ still
 * images, so single-image products are never wired up — they can't swap or
 * cycle.
 *
 * Crossfade note (cycle): the swap is still a class toggle; the crossfade is
 * pure CSS. JS adds .is-cycling to the wrapper on hover so the CSS reveals the
 * overlapping image stack (see card-product-overrides.css), and removes it a
 * beat after the pointer leaves so the off-screen images return to display:none
 * and stop loading/painting.
 */
(function () {
  'use strict';

  const initialized = new WeakSet();
  const cycleTimers = new WeakMap(); // wrapper -> setInterval id
  const cleanupTimers = new WeakMap(); // wrapper -> setTimeout id

  // Milliseconds each image is shown before advancing in cycle mode.
  const CYCLE_INTERVAL_MS = 1200;
  // Must match the crossfade transition in card-product-overrides.css
  // (--otg-transition-cycle, default 0.45s) plus a small buffer.
  const CYCLE_FADE_MS = 450;

  // Cycle is a hover affordance. On touch devices a tap fires a sticky :hover
  // that would leave the card stuck mid-cycle, so we gate cycle wiring on a
  // true pointer/hover device. (Bubbles/dots are explicit tap targets and the
  // dots reveal is CSS-driven, so they're left to the browser.)
  const CAN_HOVER =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const REDUCE_MOTION =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Show image at targetIndex, hide the rest. `indicators` is optional — when
   * present (bubbles / dots) the matching indicator gets the active class.
   */
  function switchImage(images, indicators, targetIndex) {
    images.forEach(function (img, idx) {
      if (idx === targetIndex) {
        img.classList.remove('product-card__image--hidden');
        img.classList.add('product-card__image--active');
      } else {
        img.classList.add('product-card__image--hidden');
        img.classList.remove('product-card__image--active');
      }
    });

    if (indicators && indicators.length) {
      indicators.forEach(function (indicator, idx) {
        indicator.classList.toggle('active', idx === targetIndex);
      });
    }
  }

  /* ----- Bubbles & Dots: hover an indicator to swap the image ----- */
  function setupHoverStrip(card, images, itemSelector, containerSelector) {
    const items = card.querySelectorAll(itemSelector);
    if (items.length < 2) return; // Nothing to swap to
    const container = card.querySelector(containerSelector);
    if (!container) return;

    items.forEach(function (item, idx) {
      item.addEventListener('mouseenter', function (e) {
        e.preventDefault();
        switchImage(images, items, idx);
      });

      // Keep an indicator click from bubbling up to the parent <a> (which
      // would navigate to the product page). The item is type=button already.
      item.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    // Reset to the primary image when the pointer leaves the strip.
    container.addEventListener('mouseleave', function () {
      switchImage(images, items, 0);
    });
  }

  /* ----- Cycle: timed crossfade while the card is hovered ----- */
  function clearCleanup(wrapper) {
    const t = cleanupTimers.get(wrapper);
    if (t) {
      window.clearTimeout(t);
      cleanupTimers.delete(wrapper);
    }
  }

  function setupCycle(wrapper, images) {
    if (!CAN_HOVER) return; // hover/pointer devices only
    const total = images.length;

    wrapper.addEventListener('mouseenter', function () {
      // Cancel any pending cleanup from a previous quick leave/re-enter.
      clearCleanup(wrapper);
      // Reveal the overlapping image stack so the crossfade can run.
      wrapper.classList.add('is-cycling');

      // Reduced motion: a single still swap to the second image, no timer.
      if (REDUCE_MOTION) {
        switchImage(images, null, 1);
        return;
      }

      if (cycleTimers.has(wrapper)) return; // guard against double timers

      // Commit the just-revealed (opacity:0) stack so the FIRST image change
      // crossfades instead of hard-cutting, then advance to the next image
      // immediately — no initial timer delay once the hover begins.
      void wrapper.offsetWidth; // force reflow
      let index = 1;
      switchImage(images, null, index);

      const timer = window.setInterval(function () {
        index = (index + 1) % total;
        switchImage(images, null, index);
      }, CYCLE_INTERVAL_MS);
      cycleTimers.set(wrapper, timer);
    });

    wrapper.addEventListener('mouseleave', function () {
      const timer = cycleTimers.get(wrapper);
      if (timer) {
        window.clearInterval(timer);
        cycleTimers.delete(wrapper);
      }
      switchImage(images, null, 0); // crossfade back to the primary image

      // After the fade settles, drop .is-cycling so the off-screen images
      // return to display:none and stop loading/painting.
      clearCleanup(wrapper);
      const cleanup = window.setTimeout(function () {
        wrapper.classList.remove('is-cycling');
        cleanupTimers.delete(wrapper);
      }, CYCLE_FADE_MS + 50);
      cleanupTimers.set(wrapper, cleanup);
    });
  }

  function setupWrapper(wrapper) {
    if (initialized.has(wrapper)) return;

    const mode = wrapper.getAttribute('data-image-preview');
    if (mode !== 'bubbles' && mode !== 'cycle' && mode !== 'dots') return;

    const card = wrapper.closest('.product-card') || wrapper;
    const images = card.querySelectorAll('.product-card__image--bubble');

    // Single-image products (or anything without a 2+ image stack) are never
    // wired up: nothing to swap or cycle to.
    if (images.length < 2) return;

    initialized.add(wrapper);

    if (mode === 'bubbles') {
      setupHoverStrip(card, images, '.image-bubble', '.image-bubbles');
    } else if (mode === 'dots') {
      setupHoverStrip(card, images, '.image-dot', '.image-dots');
    } else {
      setupCycle(wrapper, images);
    }
  }

  function setupAll() {
    document
      .querySelectorAll('.product-card__image-wrapper[data-image-preview]')
      .forEach(setupWrapper);
  }

  // Initial pass on DOM ready (or immediately if already past that point).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAll);
  } else {
    setupAll();
  }

  // Watch for dynamically inserted cards (AJAX paginated grids, infinite
  // scroll, filter changes, etc.).
  const observer = new MutationObserver(function (mutations) {
    let shouldRescan = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue; // element nodes only
        if (
          node.matches?.('.product-card__image-wrapper[data-image-preview]') ||
          node.querySelector?.('.product-card__image-wrapper[data-image-preview]')
        ) {
          shouldRescan = true;
          break;
        }
      }
      if (shouldRescan) break;
    }
    if (shouldRescan) setupAll();
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
